import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink, TrendingUp, Users, Target, BarChart3 } from 'lucide-react';
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

const Portfolio = () => {
  const caseStudies = [
    {
      title: "Resort Tour Increase",
      client: "Major Timeshare Resort",
      industry: "Hospitality",
      challenge: "The resort needed to increase qualified tour attendance while reducing cost per acquisition.",
      solution: "Implemented a comprehensive OPC marketing strategy with our trained team, combined with digital advertising.",
      results: [
        { metric: "45%", label: "Increase in Tours" },
        { metric: "32%", label: "Lower CPA" },
        { metric: "98%", label: "Tour Quality Score" }
      ],
      image: "https://images.pexels.com/photos/7653461/pexels-photo-7653461.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600"
    },
    {
      title: "Lead Generation Overhaul",
      client: "Regional Travel Agency",
      industry: "Travel & Tourism",
      challenge: "Struggling to generate consistent, high-quality leads for their vacation packages.",
      solution: "Deployed our lead generation systems with automated nurturing and CRM integration.",
      results: [
        { metric: "3x", label: "Lead Volume" },
        { metric: "67%", label: "Conversion Rate" },
        { metric: "4.2x", label: "ROI" }
      ],
      image: "https://images.pexels.com/photos/7580644/pexels-photo-7580644.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600"
    },
    {
      title: "Digital Transformation",
      client: "Boutique Hotel Chain",
      industry: "Hospitality",
      challenge: "Needed to modernize marketing approach and reach younger demographics.",
      solution: "Full digital advertising campaign across Google, social media, and Connected TV.",
      results: [
        { metric: "250%", label: "Website Traffic" },
        { metric: "85%", label: "Booking Increase" },
        { metric: "12x", label: "Social Engagement" }
      ],
      image: "https://images.unsplash.com/photo-1760224255774-66b3ab88bd83?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGNvcnBvcmF0ZSUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzc0NTY0ODU3fDA&ixlib=rb-4.1.0&q=85"
    }
  ];

  const stats = [
    { icon: <Target />, value: "500+", label: "Clients Served" },
    { icon: <TrendingUp />, value: "10M+", label: "Leads Generated" },
    { icon: <Users />, value: "$50M+", label: "Revenue Generated" },
    { icon: <BarChart3 />, value: "98%", label: "Client Satisfaction" }
  ];

  return (
    <div data-testid="portfolio-page">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="container-max mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4 block">
            Our Work
          </span>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-slate-900 mb-6 max-w-3xl mx-auto">
            Results That Speak for Themselves
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Explore how we've helped businesses across industries achieve exceptional 
            marketing results through our strategic approach.
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-[#0F172A] py-12">
        <div className="container-max mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="w-12 h-12 bg-[#1E3A8A] text-white rounded-xl flex items-center justify-center mx-auto mb-4">
                  {stat.icon}
                </div>
                <div className="text-3xl sm:text-4xl font-black text-white mb-2">
                  {stat.value}
                </div>
                <div className="text-slate-400 text-sm font-medium uppercase tracking-wider">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section className="section-padding bg-white" data-testid="case-studies-section">
        <div className="container-max mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4 block">
              Case Studies
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              Success Stories
            </h2>
          </div>

          <div className="space-y-12">
            {caseStudies.map((study, idx) => (
              <Card key={idx} className="border-slate-200 overflow-hidden" data-testid={`case-study-${idx}`}>
                <CardContent className="p-0">
                  <div className={`grid lg:grid-cols-2 ${idx % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                    <div className={`${idx % 2 === 1 ? 'lg:order-2' : ''}`}>
                      <img 
                        src={study.image}
                        alt={study.title}
                        className="w-full h-64 lg:h-full object-cover"
                      />
                    </div>
                    <div className={`p-8 lg:p-12 ${idx % 2 === 1 ? 'lg:order-1' : ''}`}>
                      <span className="inline-block px-3 py-1 bg-[#1E3A8A]/10 text-[#1E3A8A] text-xs font-bold uppercase tracking-wider rounded-full mb-4">
                        {study.industry}
                      </span>
                      <h3 className="text-2xl font-bold text-slate-900 mb-2">
                        {study.title}
                      </h3>
                      <p className="text-slate-500 text-sm mb-4">{study.client}</p>
                      
                      <div className="space-y-4 mb-6">
                        <div>
                          <h4 className="text-sm font-bold text-slate-700 mb-1">Challenge</h4>
                          <p className="text-slate-600 text-sm">{study.challenge}</p>
                        </div>
                        <div>
                          <h4 className="text-sm font-bold text-slate-700 mb-1">Solution</h4>
                          <p className="text-slate-600 text-sm">{study.solution}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-4">
                        {study.results.map((result, ridx) => (
                          <div key={ridx} className="text-center p-3 bg-slate-50 rounded-lg">
                            <div className="text-2xl font-black text-[#1E3A8A]">
                              {result.metric}
                            </div>
                            <div className="text-xs text-slate-500 font-medium">
                              {result.label}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="section-padding bg-slate-50">
        <div className="container-max mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4 block">
              Industries We Serve
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              Expertise Across Sectors
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { name: "Timeshare & Resorts", desc: "Our primary expertise" },
              { name: "Hospitality", desc: "Hotels & vacation rentals" },
              { name: "Travel & Tourism", desc: "Agencies & operators" },
              { name: "Real Estate", desc: "Residential & commercial" }
            ].map((industry, idx) => (
              <Card key={idx} className="bg-white border-slate-200 card-hover">
                <CardContent className="p-6 text-center">
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{industry.name}</h3>
                  <p className="text-slate-500 text-sm">{industry.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-gradient py-20">
        <div className="container-max mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-black text-white mb-6">
            Ready to Be Our Next Success Story?
          </h2>
          <p className="text-slate-300 mb-10 max-w-2xl mx-auto">
            Let's discuss how we can help you achieve similar results for your business.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/partner">
              <Button 
                size="lg" 
                className="bg-[#EA580C] hover:bg-[#c2410c] text-white px-8"
                data-testid="portfolio-cta-partner"
              >
                Become a Partner
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                size="lg" 
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 px-8"
                data-testid="portfolio-cta-contact"
              >
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Portfolio;
